/* eslint-disable no-console */
/**
 * Unified Logger for ETH Pass Portal
 *
 * This file provides:
 * 1. Global logger setup (available everywhere without import)
 * 2. Import-based logger for backward compatibility
 * 3. Context-based logging via Logger class
 *
 * Features:
 * - Automatically disabled in production for security
 * - Works in both server and client environments
 * - TypeScript support with global type declarations
 */

// Check environment - works in both server and client
const isProd = process.env.NODE_ENV === 'production'

/**
 * Logger Implementation
 * This is where the actual logger logic lives.
 * The global logger type is declared in src/types/global.d.ts
 */
// Create the logger implementation
const loggerImplementation = {
  log: (...args: any[]) => {
    if (!isProd) console.log(...args)
  },
  error: (...args: any[]) => {
    if (!isProd) console.log(...args)
  },
  warn: (...args: any[]) => {
    if (!isProd) console.log(...args)
  },
  info: (...args: any[]) => {
    if (!isProd) console.log(...args)
  },
  debug: (...args: any[]) => {
    if (!isProd) console.log(...args)
  },
  group: (...args: any[]) => {
    if (!isProd && console.group) console.group(...args)
  },
  groupEnd: () => {
    if (!isProd && console.groupEnd) console.groupEnd()
  },
  groupCollapsed: (...args: any[]) => {
    if (!isProd && console.groupCollapsed) console.groupCollapsed(...args)
  },
  table: (...args: any[]) => {
    if (!isProd && console.table) console.table(...args)
  },
  time: (label?: string) => {
    if (!isProd && console.time) console.time(label)
  },
  timeEnd: (label?: string) => {
    if (!isProd && console.timeEnd) console.timeEnd(label)
  },
}

// Set up global logger (available everywhere without import)
// Using globalThis for universal compatibility
if (typeof globalThis !== 'undefined') {
  ;(globalThis as any).logger = loggerImplementation
} else if (typeof window !== 'undefined') {
  ;(window as any).logger = loggerImplementation
} else if (typeof global !== 'undefined') {
  ;(global as any).logger = loggerImplementation
}

// Export for import-based usage (backward compatibility)
export default loggerImplementation

// For backward compatibility with files that import { Logger } or createLogger
export class Logger {
  private context: string | undefined

  constructor(context?: string) {
    this.context = context
  }

  private format(level: string, ...args: any[]): any[] {
    if (this.context) {
      return [`[${this.context}]`, ...args]
    }
    return args
  }

  log(...args: any[]): void {
    loggerImplementation.log(...this.format('LOG', ...args))
  }

  info(...args: any[]): void {
    loggerImplementation.info(...this.format('INFO', ...args))
  }

  warn(...args: any[]): void {
    loggerImplementation.warn(...this.format('WARN', ...args))
  }

  error(...args: any[]): void {
    loggerImplementation.error(...this.format('ERROR', ...args))
  }

  debug(...args: any[]): void {
    loggerImplementation.debug(...this.format('DEBUG', ...args))
  }

  group(...args: any[]): void {
    loggerImplementation.group(...args)
  }

  groupEnd(): void {
    loggerImplementation.groupEnd()
  }

  table(data: any, columns?: string[]): void {
    loggerImplementation.table(data, columns)
  }

  time(label: string): void {
    loggerImplementation.time(label)
  }

  timeEnd(label: string): void {
    loggerImplementation.timeEnd(label)
  }
}

/**
 * Create a logger with specific context
 * Uses the global logger underneath
 */
export function createLogger(context: string): Logger {
  return new Logger(context)
}
